import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {Puzzle} from '../puzzle.js';
import {BoardColor} from '../board-object.js';
import {FailStateNames} from '../../enums/FailStateNames.js';
import {GameFlowController} from '../game-flow-controller.js';
import {NotificationLevel} from '../../enums/NotificationLevel.js';
import {ScoreController} from '../score-controller.js';
import {SpecialBombType} from '../../enums/SpecialBombType.js';
import {Statistics} from '../statistics.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {AudioDirector} from '../audio-director.js';
import {Sounds} from '../../enums/Sounds.js';
import {StatNames} from '../../enums/StatNames.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {RankManager} from '../rank-manager.js';
import {timeSinceEpoch} from '../../utils/Utils.js';
import {Analytics, AnalyticsEvent} from '../../classes/Analytics.js';
import {FXPrefabs} from '../fx-prefabs.js';
import {SpawnScoreAfterSeconds} from '../spawn-score-after-seconds.js';
import {vec3} from 'gl-matrix';
import {WLUtils} from '../../utils/WLUtils.js';
import {BooleanStore} from '../boolean-store.js';

const tempVec = vec3.create();

export enum specialMode {
    FreezeDrop = 0,
}

export class GameRules extends Component {
    static TypeName = 'game-rules';
    // scoring
    @property.int()
    scoreChainIncrement: number;
    @property.int()
    scoreChainMax: number;
    @property.int()
    fruitChainIncrement: number;
    @property.int()
    fruitChainMax: number;
    @property.int()
    fruitOnlyChainValue: number;
    @property.int()
    bonusForFullObjects: number;

    // bomb scoring
    @property.int()
    bombScoreIntial: number;
    @property.float()
    bombScoreMultiplier: number;

    @property.int()
    TNTScoreInitial: number;
    @property.float()
    TNTScoreMultiplier: number;

    @property.int()
    maxLevelToWin: number;

    @property.int()
    lucky7Score: number;
    @property.int()
    sixStringScore: number;
    @property.int()
    magicEightScore: number;
    @property.int()
    blackJackScore: number;

    @property.string('')
    bonusTextObject = '';

    puzzleController: Puzzle;

    //was called chainBonusMult in the original
    chainBonus: number = 1;

    private _gameOverCause: FailStateNames;
    get failStateCause(): FailStateNames {
        return this._gameOverCause;
    }
    set failStateCause(value: FailStateNames) {
        this._gameOverCause = value;
    }

    protected _currentShapeBonus: number;
    protected _numShapeBonus: number;

    protected _gameOverState: boolean;
    get gameOverState() {
        return this._gameOverState;
    }

    private _isHighScore: boolean;
    get isHighScore(): boolean {
        return this._isHighScore;
    }

    private _isWeeklyHigh: boolean;
    get isWeeklyHigh(): boolean {
        return this._isWeeklyHigh;
    }

    private _turnsSinceStart: number;
    public get turnsSinceStart(): number {
        return this._turnsSinceStart;
    }

    private _forceGameOver: boolean;
    private _numberOfShapeBonusesInARow: number;

    public get isFirstGame(): boolean {
        return (
            Statistics.instance.getLong(StatNames.TotalGamesPlayed) === 0 &&
            Statistics.instance.getLong(StatNames.TutorialGamesPlayed) === 0
        );
    }
    start() {}

    startNewGame() {
        this._gameOverState = false;
        this._forceGameOver = false;
        this._turnsSinceStart = 0;
        this._currentShapeBonus = 100;
        this._numShapeBonus = 0;
        this._numberOfShapeBonusesInARow = 0;
        this._isHighScore = false;
        this._isWeeklyHigh = false;

        // this.puzzle.Score.ShowEasyModeHud(false);
        // this.puzzle.Score.ShowNormalModeHud(false);
        // this.puzzle.Score.ShowTimeModeHud(false);
        // this.puzzle.Score.ShowSkullModeHud(false);
    }

    /**
     *
     * @virtual
     */
    poppedBlob(color: BoardColor) {}

    /**
     *
     * @virtual
     */
    calculateScore(
        emptyBlobCount: number,
        fullBlobCount: number,
        fruitCount: number,
        bombType: SpecialBombType
    ): number {
        const totalBlobCount = emptyBlobCount + fullBlobCount;
        let score = 0;

        let blobChainValue = this.scoreChainIncrement;
        for (let x = 0; x < totalBlobCount; x++) {
            score += blobChainValue;
            blobChainValue = Math.min(
                blobChainValue + this.scoreChainIncrement,
                this.scoreChainMax
            );
        }

        for (let x = 0; x < fullBlobCount; x++) {
            score += this.bonusForFullObjects * (x + 1);
        }

        if (totalBlobCount > 0) {
            let fruitChainValue = this.fruitChainIncrement;
            for (let x = 0; x < fruitCount; x++) {
                score += fruitChainValue;
                fruitChainValue = Math.min(
                    fruitChainValue + this.fruitChainIncrement,
                    this.fruitChainMax
                );
            }
        } else {
            score += this.fruitOnlyChainValue * fruitCount;
        }

        let totalInChain = totalBlobCount + fruitCount;

        // record highest number in chain
        if (bombType == SpecialBombType.None) {
            Statistics.instance.recordHighest(StatNames.HighestNumberInChain, totalInChain);
        }

        // hack for tutorial
        if (
            bombType == SpecialBombType.CoconutBomb &&
            GameFlowController.instance.gameMode != GameModeType.Tutorial
        ) {
            score = score * this.bombScoreMultiplier;

            // Round up to multiple of 25
            score += 25 - (score % 25);

            score += this.bombScoreIntial;
        } else if (bombType == SpecialBombType.TNT) {
            score = score * this.TNTScoreMultiplier;

            // Round up to multiple of 25
            score += 25 - (score % 25);

            score += this.TNTScoreInitial;
        }

        if (totalBlobCount == 4 && fruitCount == 0) {
            if (this.bonusTextObject) {
                const targetCamera = this.engine.scene.activeViews[0].object;
                const shape = FXPrefabs.instance.spawn(this.bonusTextObject, targetCamera);
                Puzzle.instance.score.centerParent.getPositionWorld(tempVec);
                shape.setPositionWorld(tempVec);
                const shapeParams = shape.getComponent(SpawnScoreAfterSeconds);
                //GameObject shape = NGUITools.AddChild(puzzle.scoreController.centerParent, bonusTextObject);
                //shape.transform.localPosition = shape.transform.localPosition + new Vector3(0, 90f, 0) ;

                shapeParams.setTextOfThis('ShapeBonus');
                shapeParams.setScoreToShow(this._currentShapeBonus);
                shapeParams.setParent(Puzzle.instance.score.centerParent);
                shapeParams.setColor(BoardColor.Orange); //orange
                WLUtils.setActive(shape, true);
            }
            this._numShapeBonus++;

            // PREDEFINED SCORING FOR SHAPE BONUSES

            // increment for next time
            switch (this._numShapeBonus) {
                case 1:
                    this._currentShapeBonus = 250;
                    break;
                case 2:
                    this._currentShapeBonus = 500;
                    break;
                case 3:
                    this._currentShapeBonus = 1250;
                    break;
                case 4:
                    this._currentShapeBonus = 2500;
                    break;
                case 5:
                case 6:
                case 7:
                    this._currentShapeBonus += 2500;
                    break;
                case 8:
                case 9:
                    this._currentShapeBonus += 5000;
                    break;
                case 10:
                    this._currentShapeBonus = 30000;
                    break;
                case 11:
                    this._currentShapeBonus = 50000;
                    break;
                case 12:
                    this._currentShapeBonus = 100000;
                    break;

                case 13:
                    this._currentShapeBonus = 250000;
                    break;
                case 14:
                case 15:
                    this._currentShapeBonus *= 2;
                    break;
                default:
                    this._currentShapeBonus = 1000000;
                    break;
            }

            this.setShapeBonus(this._currentShapeBonus);

            Statistics.instance.recordHighest(
                StatNames.HighShapeBonus,
                this._numShapeBonus
            );

            AudioDirector.instance.playOneShotWithPriority(Sounds.shapeBonus);
            this._numberOfShapeBonusesInARow++;

            Statistics.instance.recordHighest(
                StatNames.ShapeBonusesInRow,
                this._numberOfShapeBonusesInARow
            );

            Statistics.instance.recordAdditive(StatNames.NumberShapeBonuses, 1);

            Statistics.instance.record(StatNames.LastShapeBonus, this._numShapeBonus);
        } else if (totalBlobCount == 7 && fruitCount == 0 && this.lucky7Score > 0) {
            Statistics.instance.recordAdditive(StatNames.Lucky7, 1);

            this._numberOfShapeBonusesInARow = 0;

            if (this.bonusTextObject) {
                const targetCamera = this.engine.scene.activeViews[0].object;
                const shape = FXPrefabs.instance.spawn(this.bonusTextObject, targetCamera);
                Puzzle.instance.score.centerParent.getPositionWorld(tempVec);
                shape.setPositionWorld(tempVec);
                const shapeParams = shape.getComponent(SpawnScoreAfterSeconds);
                shapeParams.setTextOfThis('Lucky7');
                shapeParams.setScoreToShow(this.lucky7Score * this.chainBonus);
                shapeParams.setParent(Puzzle.instance.score.centerParent);
                shapeParams.setColor(BoardColor.Purple); //orange
                WLUtils.setActive(shape, true);
            }

            AudioDirector.instance.playOneShotWithPriority(Sounds.lucky7);
        } else if (totalBlobCount == 6 && fruitCount == 0 && this.sixStringScore > 0) {
            this._numberOfShapeBonusesInARow = 0;

            if (this.bonusTextObject) {
                const targetCamera = this.engine.scene.activeViews[0].object;
                const shape = FXPrefabs.instance.spawn(this.bonusTextObject, targetCamera);
                Puzzle.instance.score.centerParent.getPositionWorld(tempVec);
                shape.setPositionWorld(tempVec);
                const shapeParams = shape.getComponent(SpawnScoreAfterSeconds);
                shapeParams.setTextOfThis('SixString');
                shapeParams.setScoreToShow(this.sixStringScore * this.chainBonus);
                shapeParams.setParent(Puzzle.instance.score.centerParent);
                shapeParams.setColor(BoardColor.Pink); //orange
                WLUtils.setActive(shape, true);
            }

            AudioDirector.instance.playOneShotWithPriority(Sounds.sixString);
        } else if (totalBlobCount == 8 && fruitCount == 0 && this.magicEightScore > 0) {
            this._numberOfShapeBonusesInARow = 0;

            if (this.bonusTextObject) {
                const targetCamera = this.engine.scene.activeViews[0].object;
                const shape = FXPrefabs.instance.spawn(this.bonusTextObject, targetCamera);
                Puzzle.instance.score.centerParent.getPositionWorld(tempVec);
                shape.setPositionWorld(tempVec);
                const shapeParams = shape.getComponent(SpawnScoreAfterSeconds);
                shapeParams.setTextOfThis('Magic8');
                shapeParams.setScoreToShow(this.magicEightScore * this.chainBonus);
                shapeParams.setParent(Puzzle.instance.score.centerParent);
                shapeParams.setColor(BoardColor.Purple);
                WLUtils.setActive(shape, true);
            }
            AudioDirector.instance.playOneShotWithPriority(Sounds.magic8);
        } else if (totalBlobCount == 21 && fruitCount == 0 && this.blackJackScore > 0) {
            this._numberOfShapeBonusesInARow = 0;
            if (this.bonusTextObject) {
                const targetCamera = this.engine.scene.activeViews[0].object;
                const shape = FXPrefabs.instance.spawn(this.bonusTextObject, targetCamera);
                Puzzle.instance.score.centerParent.getPositionWorld(tempVec);
                shape.setPositionWorld(tempVec);
                const shapeParams = shape.getComponent(SpawnScoreAfterSeconds);
                shapeParams.setTextOfThis('Blackjack');
                shapeParams.setScoreToShow(this.blackJackScore * this.chainBonus);
                shapeParams.setParent(Puzzle.instance.score.centerParent);
                shapeParams.setColor(BoardColor.Moon);
                WLUtils.setActive(shape, true);
            }

            AudioDirector.instance.playOneShotWithPriority(Sounds.blackJack);
        } else {
            this._numberOfShapeBonusesInARow = 0;
        }

        return score;
    }

    /**
     *
     * @virtual
     */
    timeToChangeStack(): boolean {
        return false;
    }

    /**
     *
     * @virtual
     */
    changeStack(): boolean {
        return false;
    }

    /**
     *
     * @virtual
     */
    detectGameOver(): boolean {
        return this._forceGameOver;
    }

    /**
     *
     * @virtual
     */
    turnComplete() {
        this._turnsSinceStart++;
    }

    /**
     *
     * @virtual
     */
    warnStackChange(): boolean {
        return false;
    }

    /**
     * @virtual
     */
    showFinalResults() {}

    /**
     *
     * @virtual
     */
    incrementGamesPlayed() {}

    /**
     *
     * @virtual
     */
    addTime(time: number) {}

    /**
     *
     * @virtual
     */
    timeRemaining(): number {
        return 0.0;
    }

    /**
     *
     * @virtual
     */
    gameOver(failstate: FailStateNames) {
        GameFlowController.instance.notificationsAllowed = NotificationLevel.None;

        this._gameOverState = true;

        if (
            failstate != FailStateNames.CountdownExpired &&
            failstate != FailStateNames.TimeUp &&
            failstate != FailStateNames.MaxLevelReached
        ) {
            this.playGameOverSFX();
        }

        const scoreController = ScoreController.instance;

        // add new coins to the total
        if (scoreController.coins > 0) {
            Statistics.instance.addAnonymousPurchaseTransaction(
                TransactionalNames.CoinCount,
                scoreController.coins
            );
        }

        Analytics.LogEventWithValue(
            AnalyticsEvent.EarnedCoins,
            scoreController.coins,
            'GameOver'
        );
        Analytics.LogEventWithValue(
            AnalyticsEvent.ShapeBonusesPerGame,
            this._numShapeBonus
        );

        const topHighScore = this.getHighScore();
        const topWeeklyScore = this.getWeeklyHighScore();
        const score = scoreController.score;

        this.saveCumulativeScore(score);

        Statistics.instance.record(StatNames.LastGameScore, score);

        this.saveHighScore(score);

        // show notification if you beat your high score
        if (scoreController.score > topHighScore) {
            if (topHighScore != 0) {
                this._isHighScore = true;
            }
        } else if (scoreController.score > topWeeklyScore) {
            if (topWeeklyScore != 0) {
                this._isWeeklyHigh = true;
            }
        }

        const rankManager = RankManager.instance;
        const initialXp = rankManager.currentRankXp;
        const initialRank = rankManager.rank;

        const newXP = rankManager.scoreToXp(score);

        rankManager.addXp(newXP, false);

        // // Set lastest combo in stats
        Statistics.instance.record(StatNames.LastGameCombo, scoreController.combo);
        Statistics.instance.record(StatNames.LastGameLevel, scoreController.level);
        Statistics.instance.recordHighest(StatNames.HighestLevel, scoreController.level);

        const mode = GameFlowController.instance.gameMode;
        switch (mode) {
            case GameModeType.Easy:
                Analytics.LogEventWithValue(
                    AnalyticsEvent.LevelReachedNormal,
                    scoreController.level
                );
                Statistics.instance.recordHighest(
                    StatNames.HighestLevelNormal,
                    scoreController.level
                );
                break;
            case GameModeType.Normal:
                Analytics.LogEventWithValue(
                    AnalyticsEvent.LevelReachedExpert,
                    scoreController.level
                );
                Statistics.instance.recordHighest(
                    StatNames.HighestLevelExpert,
                    scoreController.level
                );
                break;
            case GameModeType.Timed:
                Analytics.LogEventWithValue(
                    AnalyticsEvent.LevelReachedTimed,
                    scoreController.level
                );
                Statistics.instance.recordHighest(
                    StatNames.HighestLevelTimed,
                    scoreController.level
                );
                break;
            case GameModeType.Survival:
                Analytics.LogEventWithValue(
                    AnalyticsEvent.LevelReachedSkull,
                    scoreController.level
                );
                Statistics.instance.recordHighest(
                    StatNames.HighestLevelSkull,
                    scoreController.level
                );
                break;
            case GameModeType.Fungus:
                Analytics.LogEventWithValue(
                    AnalyticsEvent.LevelReachedFungus,
                    scoreController.level
                );
                Statistics.instance.recordHighest(
                    StatNames.HighestLevelFungus,
                    scoreController.level
                );
                break;
            case GameModeType.Cupcake:
                Analytics.LogEventWithValue(
                    AnalyticsEvent.LevelReachedCupcake,
                    scoreController.level
                );
                Statistics.instance.recordHighest(
                    StatNames.HighestLevelCupcake,
                    scoreController.level
                );
                break;
        }

        this.showFinalResults();

        this.incrementGamesPlayed();

        this.puzzleController.score.setupGameOverResults(
            this.isHighScore,
            this.isWeeklyHigh,
            initialRank,
            initialXp,
            newXP
        );

        // Set the stat for last game mode played
        const gameModeEnum = GameFlowController.instance.gameMode;
        Statistics.instance.record(StatNames.LastGameModePlayed, gameModeEnum);
        Statistics.instance.record(StatNames.LastGameTimestamp, timeSinceEpoch());
    }

    /**
     * @virtual
     */
    addCoins(coinCount: number, object: Object3D) {
        this.puzzleController.score.addCoins(coinCount);
    }

    /**
     * @virtual
     */
    forceGameOver(gameOver: FailStateNames) {
        // set enum for FailStateName
        this._gameOverCause = gameOver;
        this._forceGameOver = true;
    }
    /**
     *@virtual
     */
    addSpecial(amount: number, blob: Object3D) {}

    /**
     *@virtual
     */
    saveCumulativeScore(score: number) {}

    /**
     *@virtual
     */
    getHighScore(): number {
        return 0;
    }

    /**
     *@virtual
     */
    getWeeklyHighScore(): number {
        return 0;
    }

    /**
     *@virtual
     */
    saveHighScore(score: number) {}

    /**
     *@virtual
     */
    setShapeBonus(shapeBonus: number) {
        this.puzzleController.score.showShapeCount(this._numShapeBonus);
    }

    /**
     * @virtual
     */
    showHUD(): boolean {
        return true;
    }

    /**
     * @virtual
     */
    isCoconutEmergency(): boolean {
        return false;
    }

    /**
     * @virtual
     */
    checkCoconutEmergency(): boolean {
        return false;
    }

    playGameOverSFX() {
        AudioDirector.instance.playOneShot(Sounds.gameOver, true);
    }

    /**
     * @virtual
     */
    fruitPreSpawn(obj: Object3D): Object3D {
        return obj;
    }

    /**
     * @virtual
     */
    preferColor(color: BoardColor): BoardColor | null {
        return null;
    }
}
